import { PaginatedDataDTO, PaginationDTO, PaginationRestDTO } from '@bottega52/commons-pagination';
import * as MksAPI from '../../repository/jago/mksAPI';
import { IState } from '../store';
import { ActionsUnion, createAction, IThunkAction } from "../utils";
import MksActionTypesEnum from "./model/MksActionTypesEnum";
import { ICylinderInDTO } from '../../repository/jago/model/input/ICylinderInDTO';
import { IKeyInDTO } from '../../repository/jago/model/input/IKeyInDTO';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import { ICsvPreviewInDTO } from '../../repository/jago/model/input/ICsvPreviewInDTO';
import { IMechanicalCylinderKeyRelationInDTO } from '../../repository/jago/model/input/IMechanicalCylinderKeyRelationInDTO';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { ICylindersRequestParamsDTO, IKeysRequestParamsDTO, IMksImportsRequestParamsDTO, IMechanicalCylinderKeyRelationsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { 
  IMKSRequestDTO, 
  IMKSRequestInDTO, 
  IMKSRequestUpdateDTO, 
  IMKSRequestStatusChangeDTO, 
  IMKSRequestParamsDTO 
} from '../../repository/jago/model/output/IMKSRequestDTO';
import _ from 'lodash';

export const MksActions = {
  saveCylinders: createAction<typeof MksActionTypesEnum.SAVE_CYLINDERS, PaginatedDataDTO<ICylinderInDTO>>(MksActionTypesEnum.SAVE_CYLINDERS),
  setSelectedCylinder: createAction<typeof MksActionTypesEnum.SET_SELECTED_CYLINDER, ICylinderInDTO | {}>(MksActionTypesEnum.SET_SELECTED_CYLINDER),
  saveKeys: createAction<typeof MksActionTypesEnum.SAVE_KEYS, PaginatedDataDTO<IKeyInDTO>>(MksActionTypesEnum.SAVE_KEYS),
  setSelectedKey: createAction<typeof MksActionTypesEnum.SET_SELECTED_KEY, IKeyInDTO | {}>(MksActionTypesEnum.SET_SELECTED_KEY),
  setSelectedHostname: createAction<typeof MksActionTypesEnum.SET_SELECTED_HOSTNAME, string>(MksActionTypesEnum.SET_SELECTED_HOSTNAME),
  setSelectedDomain: createAction<typeof MksActionTypesEnum.SET_SELECTED_DOMAIN, IDomainOptionDTO | null>(MksActionTypesEnum.SET_SELECTED_DOMAIN),
  saveImports: createAction<typeof MksActionTypesEnum.SAVE_IMPORTS, PaginatedDataDTO<IMksImportInDTO>>(MksActionTypesEnum.SAVE_IMPORTS),
  setSelectedImport: createAction<typeof MksActionTypesEnum.SET_SELECTED_IMPORT, IMksImportInDTO | {}>(MksActionTypesEnum.SET_SELECTED_IMPORT),
  saveLastImport: createAction<typeof MksActionTypesEnum.SAVE_LAST_IMPORT, IMksImportInDTO | {}>(MksActionTypesEnum.SAVE_LAST_IMPORT),
  saveCsvPreview: createAction<typeof MksActionTypesEnum.SAVE_CSV_PREVIEW, ICsvPreviewInDTO>(MksActionTypesEnum.SAVE_CSV_PREVIEW),
  clearCsvPreview: createAction<typeof MksActionTypesEnum.CLEAR_CSV_PREVIEW, {}>(MksActionTypesEnum.CLEAR_CSV_PREVIEW),
  saveRelations: createAction<typeof MksActionTypesEnum.SAVE_RELATIONS, PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO>>(MksActionTypesEnum.SAVE_RELATIONS),
  resetMksData: createAction<typeof MksActionTypesEnum.RESET_MKS_DATA, void>(MksActionTypesEnum.RESET_MKS_DATA),
  saveMksRequests: createAction<typeof MksActionTypesEnum.SAVE_MKS_REQUESTS, PaginatedDataDTO<IMKSRequestDTO>>(MksActionTypesEnum.SAVE_MKS_REQUESTS),
  setSelectedMksRequest: createAction<typeof MksActionTypesEnum.SET_SELECTED_MKS_REQUEST, IMKSRequestDTO | {}>(MksActionTypesEnum.SET_SELECTED_MKS_REQUEST),
  saveMksRequestDetail: createAction<typeof MksActionTypesEnum.SAVE_MKS_REQUEST_DETAIL, IMKSRequestDTO>(MksActionTypesEnum.SAVE_MKS_REQUEST_DETAIL),
  updateMksRequest: createAction<typeof MksActionTypesEnum.UPDATE_MKS_REQUEST, IMKSRequestDTO>(MksActionTypesEnum.UPDATE_MKS_REQUEST),
  deleteMksRequest: createAction<typeof MksActionTypesEnum.DELETE_MKS_REQUEST, IMKSRequestDTO>(MksActionTypesEnum.DELETE_MKS_REQUEST),
  changeMksRequestStatus: createAction<typeof MksActionTypesEnum.CHANGE_MKS_REQUEST_STATUS, IMKSRequestDTO>(MksActionTypesEnum.CHANGE_MKS_REQUEST_STATUS),
  exportMksRequests: createAction<typeof MksActionTypesEnum.EXPORT_MKS_REQUESTS, IMKSRequestDTO[]>(MksActionTypesEnum.EXPORT_MKS_REQUESTS),
  resetMksRequestsData: createAction<typeof MksActionTypesEnum.RESET_MKS_REQUESTS_DATA, void>(MksActionTypesEnum.RESET_MKS_REQUESTS_DATA),
  resetMksCylindersData: createAction<typeof MksActionTypesEnum.RESET_MKS_CYLINDERS_DATA, void>(MksActionTypesEnum.RESET_MKS_CYLINDERS_DATA),
  resetMksKeysData: createAction<typeof MksActionTypesEnum.RESET_MKS_KEYS_DATA, void>(MksActionTypesEnum.RESET_MKS_KEYS_DATA),
  resetMksRelationsData: createAction<typeof MksActionTypesEnum.RESET_MKS_RELATIONS_DATA, void>(MksActionTypesEnum.RESET_MKS_RELATIONS_DATA),
  resetMksImportsData: createAction<typeof MksActionTypesEnum.RESET_MKS_IMPORTS_DATA, void>(MksActionTypesEnum.RESET_MKS_IMPORTS_DATA),
};

export type MksActionsType = ActionsUnion<typeof MksActions>;

// Helper function to convert PaginationRestDTO to PaginatedDataDTO
function convertPaginationData<T>(data: PaginationRestDTO<T>): PaginatedDataDTO<T> {
  const pagination: PaginationDTO = _.omit(data, 'content');
  return {
    content: data.content,
    pagination,
  };
}

export function fetchCylinders(params: ICylindersRequestParamsDTO): IThunkAction<Promise<PaginatedDataDTO<ICylinderInDTO>>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchCylinders(params);
      if (response && response.data) {
        const convertedData = convertPaginationData(response.data);
        dispatch(MksActions.saveCylinders(convertedData));
        return convertedData;
      } else {
        dispatch(MksActions.saveCylinders({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
        throw new Error();
      }
    } catch (error) {
      dispatch(MksActions.saveCylinders({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
      throw error;
    }
  };
}

export function fetchKeys(params: IKeysRequestParamsDTO): IThunkAction<Promise<PaginatedDataDTO<IKeyInDTO>>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchKeys(params);
      if (response && response.data) {
        const convertedData = convertPaginationData(response.data);
        dispatch(MksActions.saveKeys(convertedData));
        return convertedData;
      } else {
        dispatch(MksActions.saveKeys({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
        throw new Error();
      }
    } catch (error) {
      dispatch(MksActions.saveKeys({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
      throw error;
    }
  };
}

export function fetchMksDataForSelectedDomain(): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    const { selectedDomain } = getState().mks;
    if (!selectedDomain) return;
    try {
    await dispatch(fetchCylinders({ hostname: selectedDomain.value.hostname, page: 0, pageSize: 200 }));
    } catch (error) {}
    try {
      await dispatch(fetchKeys({ hostname: selectedDomain.value.hostname, page: 0, pageSize: 200 }));
    } catch (error) {}
    try {
      await dispatch(fetchMechanicalCylinderKeyRelations({ hostname: selectedDomain.value.hostname, page: 0, pageSize: 10000 }));
    } catch (error) {}
    try {
      await dispatch(fetchLastMksImport({ hostname: selectedDomain.value.hostname }));
    } catch (error) {}
  };
}

export function setSelectedCylinder(cylinder: ICylinderInDTO | {}): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.setSelectedCylinder(cylinder));
  }
}

export function setSelectedKey(key: IKeyInDTO | {}): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.setSelectedKey(key));
  }
}

export function setSelectedHostname(hostname: string): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.setSelectedHostname(hostname));
  }
}

export function setSelectedDomain(domain: IDomainOptionDTO | null): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.setSelectedDomain(domain));
    // Quando si seleziona un dominio, aggiorna anche l'hostname
    if (domain) {
      dispatch(MksActions.setSelectedHostname(domain.value.hostname));
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
    } else {
      dispatch(MksActions.saveLastImport({}));
      dispatch(MksActions.setSelectedHostname(''));
      localStorage.removeItem('masterKeyDomain');
    }
  }
}

export function fetchMksImports(params: IMksImportsRequestParamsDTO): IThunkAction<Promise<PaginatedDataDTO<IMksImportInDTO>>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchMksImports(params);
      if (response && response.data) {
        const convertedData = convertPaginationData(response.data);
        dispatch(MksActions.saveImports(convertedData));
        return convertedData;
      } else {
        dispatch(MksActions.saveImports({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
        throw new Error();
      }
    } catch (error) {
      dispatch(MksActions.saveImports({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
      throw error;
    }
  };
}

export function fetchLastMksImport(params: IMksImportsRequestParamsDTO): IThunkAction<Promise<IMksImportInDTO | null>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchMksImports(params);
      if (response && response.data && response.data.content && response.data.content.length > 0) {
        const lastImport = response.data.content[0];
        dispatch(MksActions.saveLastImport(lastImport));
        return lastImport;
      } else {
        dispatch(MksActions.saveLastImport({}));
        return null;
      }
    } catch (error) {
      dispatch(MksActions.saveLastImport({}));
      throw error;
    }
  };
}

export function previewCsvData(hostname: string, fileFormat: string, file: File): IThunkAction<Promise<ICsvPreviewInDTO>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.previewCsvData(hostname, fileFormat, file);
      if (response && response.data) {
        dispatch(MksActions.saveCsvPreview(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function importMechanicalCylindersFromCsv(hostname: string, fileFormat: string, file: File): IThunkAction<Promise<boolean>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.importMechanicalCylindersFromCsv(hostname, fileFormat, file);
      if (response && response.status === 201) {
        // Dopo un import riuscito, pulisci l'anteprima e ricarica la lista degli import
        dispatch(MksActions.clearCsvPreview({}));
        return true;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function setSelectedImport(importItem: IMksImportInDTO | {}): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.setSelectedImport(importItem));
  }
}

export function clearCsvPreview(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.clearCsvPreview({}));
  }
}

export function resetMksData(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.resetMksData());
  }
}

export function fetchMechanicalCylinderKeyRelations(params: IMechanicalCylinderKeyRelationsRequestParamsDTO): IThunkAction<Promise<PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO>>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchMechanicalCylinderKeyRelations(params);
      if (response && response.data) {
        const convertedData = convertPaginationData(response.data);
        dispatch(MksActions.saveRelations(convertedData));
        return convertedData;
      } else {
        dispatch(MksActions.saveRelations({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
        throw new Error();
      }
    } catch (error) {
      dispatch(MksActions.saveRelations({ content: [], pagination: { page: 0, pageSize: 0, totalElements: 0, totalPages: 0 } }));
      throw error;
    }
  };
}

export function fetchMKSRequests(params: IMKSRequestParamsDTO): IThunkAction<Promise<PaginatedDataDTO<IMKSRequestDTO>>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchMKSRequests(params);
      if (response && response.data) {
        const convertedData = convertPaginationData(response.data);
        dispatch(MksActions.saveMksRequests(convertedData));
        return convertedData;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function fetchMKSRequestById(requestId: number): IThunkAction<Promise<IMKSRequestDTO>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.fetchMKSRequestById(requestId);
      if (response && response.data) {
        dispatch(MksActions.saveMksRequestDetail(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function createMKSRequest(data: IMKSRequestInDTO): IThunkAction<Promise<IMKSRequestDTO>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.createMKSRequest(data);
      if (response && response.data) {
        dispatch(MksActions.saveMksRequestDetail(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function updateMKSRequest(requestId: number, data: IMKSRequestUpdateDTO): IThunkAction<Promise<IMKSRequestDTO>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.updateMKSRequest(requestId, data);
      if (response && response.data) {
        dispatch(MksActions.updateMksRequest(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function changeMKSRequestStatus(requestId: number, data: IMKSRequestStatusChangeDTO): IThunkAction<Promise<IMKSRequestDTO>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.changeMKSRequestStatus(requestId, data);
      if (response && response.data) {
        dispatch(MksActions.changeMksRequestStatus(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function deleteMKSRequest(requestId: number): IThunkAction<Promise<IMKSRequestDTO>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.deleteMKSRequest(requestId);
      if (response && response.data) {
        dispatch(MksActions.deleteMksRequest(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function exportMKSRequests(params: Omit<IMKSRequestParamsDTO, 'page' | 'pageSize'>): IThunkAction<Promise<IMKSRequestDTO[]>, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await MksAPI.exportMKSRequests(params);
      if (response && response.data) {
        dispatch(MksActions.exportMksRequests(response.data));
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw error;
    }
  };
}

export function setSelectedMksRequest(request: IMKSRequestDTO | {}): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.setSelectedMksRequest(request));
  };
}

export function resetMksRequestsData(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.resetMksRequestsData());
  };
}

export function resetMksCylindersData(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.resetMksCylindersData());
  };
}

export function resetMksKeysData(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.resetMksKeysData());
  };
}

export function resetMksImportsData(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.resetMksImportsData());
  };
}

export function resetMksRelationsData(): IThunkAction<void, IState> {
  return (dispatch) => {
    dispatch(MksActions.resetMksRelationsData());
  };
}